import { Box, Typography, ChipProps, IconButton } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';

import { useTranslation } from 'react-i18next';
import { PeopleIcon, PersonIcon, InfoIcon, EditIcon } from '../../../icons';
import { BenefitIcon, BenefitKey } from '../../compound/dataDisplay';
import { CustomBenefitIcon } from '../../compound/dataDisplay/customBenefitIcon';
import {
  BenefitSet,
  CompanyBenefitSettings,
  EnabledBenefitsEnum,
  IconEnum
} from '@pxtra-client-lib-legacy/gen';
import { Chip } from '../../atomic';
import { BenefitSetCell } from './cells/benefit-set-cell';
import { getNextMonthHeaderSuffix } from '../shared/utils';

export type BenefitsSetRowItem = {
  id: string | keyof CompanyBenefitSettings;
  name: string;
  icon?: IconEnum;
  custom?: boolean;
  entitledForBenefitCount: number;
  benefitInBenefitSetCount: number;
  activeUsageCount: number;
};

const columnHelper = createColumnHelper<BenefitsSetRowItem>();

export const createPortfolioColumns = (
  onInfoClick: (benefitId: EnabledBenefitsEnum) => void,
  onEditClick: (row: BenefitsSetRowItem) => void,
  benefitSets: BenefitSet[]
) => {
  const { t, i18n } = useTranslation();

  return [
    columnHelper.accessor('name', {
      header: t('common:BENEFIT', { count: 1 }),
      cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {!row.original.custom && (
            <BenefitIcon
              benefitKey={row.original.id as BenefitKey}
              size={32}
              sx={{ mr: 2, borderRadius: '8px !important' }}
            />
          )}
          {row.original.custom && (
            <CustomBenefitIcon
              iconKey={row.original.icon ?? 'apple'}
              size={32}
              sx={{ mr: 2, borderRadius: '8px !important' }}
            />
          )}
          <Typography>
            {row.original.custom ? row.original.name : t(`benefits:${row.original.id}`)}
          </Typography>
        </Box>
      ),
      meta: {
        filterVariant: 'select'
      }
    }),
    columnHelper.accessor('benefitInBenefitSetCount', {
      header: t('benefitSet:benefitSet_one'),
      cell: ({ row }) => <BenefitSetCell row={row} benefitSets={benefitSets} />,
      meta: {
        filterVariant: 'select',
        filterOptions:
          benefitSets.map((set) => ({
            value: set.name ?? '',
            text: set.name ?? ''
          })) || []
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || filterValue.length === 0) return true;

        const selectedSetNames = Array.isArray(filterValue) ? filterValue : [filterValue];

        return selectedSetNames.some((setName) => {
          const selectedSet = benefitSets?.find((set) => set.name === setName);
          return (
            selectedSet?.enabled_benefits?.includes(row.original.id as EnabledBenefitsEnum) || false
          );
        });
      },
      enableColumnFilter: true,
      enableGlobalFilter: true
    }),

    columnHelper.accessor('entitledForBenefitCount', {
      header: t('common:ENTITLED') + getNextMonthHeaderSuffix(i18n),
      cell: ({ row }) => (
        <Box className="flex items-center gap-2">
          <PeopleIcon className="[&_path]:fill-none" />
          <Typography className="font-medium">{row.original.entitledForBenefitCount}</Typography>
        </Box>
      ),
      enableColumnFilter: false
    }),
    columnHelper.accessor('activeUsageCount', {
      header: t('common:USAGE') + getNextMonthHeaderSuffix(i18n),
      cell: ({ row }) => {
        if (row.original.custom) {
          return <Typography>-</Typography>;
        }
        const activeUsage = row.original.activeUsageCount || 0;
        const entitled = row.original.entitledForBenefitCount || 0;
        const percentage = entitled > 0 ? Math.round((activeUsage / entitled) * 100) : 0;

        return (
          <Box className="flex items-center gap-2">
            <Box className="flex items-center gap-2">
              <PersonIcon className="[&_path]:fill-none" />
              <Typography className="font-medium">{activeUsage}</Typography>
            </Box>
            <Typography color="grey.600" className="text-sm">
              ({percentage}%)
            </Typography>
          </Box>
        );
      },
      enableColumnFilter: false
    }),
    columnHelper.accessor('custom', {
      header: t('common:IMPLEMENTATION'),
      cell: ({ row }) => {
        const label = row.original.custom ? t('common:CUSTOM') : 'pxtra';
        const color: ChipProps['color'] = row.original.custom ? 'heaven' : 'secondary';

        return <Chip label={label} color={color} />;
      },
      meta: {
        filterVariant: 'select',
        filterOptions: [
          { value: 'false', text: 'Pxtra' },
          { value: 'true', text: 'Custom' }
        ]
      },
      filterFn: (row, columnId, filterValue) => {
        const rowValue = row.getValue(columnId);
        const booleanFilterValue = filterValue === 'true';
        return rowValue === booleanFilterValue;
      },
      enableColumnFilter: true,
      enableGlobalFilter: true
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common:ACTIONS'),
      cell: ({ row }) => {
        const contractBenefits = ['bkv', 'bav', 'jobbike', 'mpp'];

        if (contractBenefits.includes(row.original.id)) {
          return (
            <IconButton onClick={() => onInfoClick(row.original.id as EnabledBenefitsEnum)}>
              <InfoIcon color="grey.600" />
            </IconButton>
          );
        } else if (row.original.custom) {
          return (
            <IconButton onClick={() => onEditClick(row.original)}>
              <EditIcon color="grey.600" />
            </IconButton>
          );
        }
        return <Typography className="grey-600 pl-4"> - </Typography>;
      }
    })
  ];
};
